import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

// Matches the server's neutral generic (see branding() in server/lib.php) so the tab title and the
// sidebar/login brand block never flash — or fall back to — a different placeholder than the API itself
// would resolve to before GET /branding answers.
export const DEFAULT_APP_NAME = "Cash Ledger";
const DEFAULT_BRANDING = { company_name: "", app_name: DEFAULT_APP_NAME, tagline: "", has_logo: false };

const BrandingContext = createContext({ ...DEFAULT_BRANDING, logoUrl: null, loading: true, refresh: () => {} });

/** Fetches GET /branding once at boot — public, so it works before anyone signs in — and makes the
 *  product's white-label identity (company name, app name, tagline, logo) available everywhere: the
 *  sidebar brand block, the login page, and document.title. */
export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      setBranding(data);
    } catch {
      setBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const name = branding.app_name || DEFAULT_APP_NAME;
    document.title = name;
    // iOS reads this tag's live content at the moment someone taps Add to Home Screen — same as it reads
    // document.title — so the PWA install caption on iOS never drifts from the tab title above it.
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", name);
  }, [branding.app_name]);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    if (branding.has_logo) {
      api.get("/branding/logo", { responseType: "blob" }).then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setLogoUrl(url);
      }).catch(() => { if (!cancelled) setLogoUrl(null); });
    } else {
      setLogoUrl(null);
    }
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [branding.has_logo]);

  return (
    <BrandingContext.Provider value={{ ...branding, logoUrl, loading, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
